import flow from 'lodash/flow';
import get from 'lodash/get';
import identity from 'lodash/identity';
import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';
import {
  IMultiLicenseDisplayConfigItem,
  PostProcessingMethods,
} from '../license-header/types';
import { TMultiLicenseDataDisplay } from './multi-license-data-display-types';

function maybeFallbackToDefault<T>(
  value: T,
  defaultValue?: string
): T | string | number {
  const useDefaultValue =
    !isNil(defaultValue) && (isNil(value) || isEmpty(value));
  return useDefaultValue ? defaultValue : value;
}

/**
 * Post-processors available to the "MultiLicenseDataDisplayComponent" component
 */
function simpleJoin(array: unknown[] = []): string {
  if (Array.isArray(array) && !isEmpty(array)) return array.join(', ');
  return 'N/A';
}

const postProcessors: Record<
  PostProcessingMethods,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (retrievedValueFromDataStore: any) => string | number
> = {
  join: simpleJoin,
};

/**
 * Functions that aid the "MultiLicenseDataDisplayComponent" component in retrieving
 * the correct information and apply requests transformations (when needed) to provide
 * the component with the correct data to render.
 */
type DataStore = TMultiLicenseDataDisplay['dataStore'];

type StoreGetterReturnType = (args: string) => string;

function storeGetter(store: DataStore): StoreGetterReturnType {
  return store === 'IN_PROGRESS'
    ? () => 'N/A'
    : (path: string) => get(store, path, '');
}

type MultiLicenseFlowProcessorReturnType = (
  args: IMultiLicenseDisplayConfigItem
) => string | number;

export function multiLicenseFlowProcessor(
  store: DataStore
): MultiLicenseFlowProcessorReturnType {
  return function innerRetrieveDataFromStore(
    args: IMultiLicenseDisplayConfigItem
  ) {
    return flow(
      storeGetter(store),
      postProcessors[args?.method as PostProcessingMethods] ?? identity,
      (value: string | number) =>
        maybeFallbackToDefault<string | number>(value, args.defaultValue)
    )(args.path);
  };
}
